const SITEMAP_ENVIRONMENTS = ['local', 'dev', 'prod'] as const;
type SitemapEnvironment = (typeof SITEMAP_ENVIRONMENTS)[number];
export type LogoVariant = 'v2';

export function resolveLogoVariant(
  value: string | undefined,
): LogoVariant | undefined {
  return value?.trim() === 'v2' ? 'v2' : undefined;
}

/**
 * Resolves the sitemap source environment from `SITEMAP_ENVIRONMENT`.
 * Falls back to the current ENVIRONMENT-based behavior when unset/invalid.
 */
function resolveSitemapEnvironment(
  value: string | undefined,
  isProd: boolean,
): SitemapEnvironment {
  if (value && SITEMAP_ENVIRONMENTS.includes(value as SitemapEnvironment)) {
    return value as SitemapEnvironment;
  }

  return isProd ? 'prod' : 'dev';
}

/**
 * A service base URL that may carry a session token. Anything but https is
 * dropped, except http in local development and tests. Empty means "not
 * configured", and callers must not guess a host.
 */
export function resolveHttpsBaseUrl(
  value: string | undefined,
  allowHttp: boolean,
): string {
  const trimmed = value?.trim();
  if (!trimmed) return '';
  try {
    const { protocol } = new URL(trimmed);
    if (protocol === 'https:' || (allowHttp && protocol === 'http:')) {
      return trimmed;
    }
  } catch {
    return '';
  }
  return '';
}

function positiveInt(value: string | undefined, fallback: number): number {
  const parsed = Number.parseInt(value ?? '', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

export const env = {
  appVersion:
    process.env.NEXT_PUBLIC_APP_VERSION ||
    process.env.HEADLESS_METADATA_COMMIT_SHA ||
    'local',
  strictValidation: process.env.STRICT_API_VALIDATION === 'true',
  isDebugAllowed: process.env.NEXT_PUBLIC_IS_DEBUG_ALLOWED === 'true',

  /**
   * New Relic — enabled by default when a license key is present (NEW_RELIC_ENABLED !== 'false').
   * When no key (e.g. CI/build), always false so we never wait for the agent or inject the snippet.
   */
  newRelicEnabled:
    process.env.NEW_RELIC_ENABLED !== 'false' &&
    Boolean(process.env.NEW_RELIC_LICENSE_KEY),

  /**
   * Tenant Configuration
   */
  tenantsApiUrl: process.env.NEXT_PUBLIC_TENANTS_API_URL || '',
  logoVariant: resolveLogoVariant(process.env.LOGO_VARIANT),
  forceTenant: process.env.FORCE_TENANT || '',
  tenantResolution: process.env.TENANT_RESOLUTION || 'path',
  isProd: process.env.ENVIRONMENT === 'prd',
  isDev:
    process.env.NODE_ENV === 'development' ||
    !(process.env.ENVIRONMENT === 'prd'),
  /** `next dev` only; deployed non-prod stages are `isDev` but not this. */
  isLocalDev: process.env.NODE_ENV === 'development',
  environment: process.env.ENVIRONMENT || 'development',
  sitemapEnvironment: resolveSitemapEnvironment(
    process.env.SITEMAP_ENVIRONMENT,
    process.env.ENVIRONMENT === 'prd',
  ),
  cmsUrlOverride: process.env.CMS_URL_OVERRIDE || '',

  /**
   * Logging
   */
  logLevel: process.env.NEXT_PUBLIC_LOG_LEVEL || 'info',
  prettyPrintLogs: process.env.PRETTY_PRINT_LOGS === 'true',
  redirectServiceApi:
    process.env.REDIRECT_SERVICE_API ||
    (process.env.ENVIRONMENT === 'prd'
      ? 'https://redirects.api.highereducation.com'
      : 'https://redirects.int.api.highereducation.com'),
  /** Base URL for dynamic experience / flow API (used by `@highereducation/dynamic-experience`). */
  dynamicFlowApiUrl:
    process.env.NEXT_PUBLIC_DYNAMIC_FLOW_API ||
    'https://dynamic-flows.int.api.highereducation.com',

  /**
   * Learners-hub production switch. Exact `'true'` keeps the placement
   * production already ships (`aboveTheFold`) and the slow-load message.
   * The host is still React `DynamicExperience` and still receives the
   * request snapshot. This does not render `<he-dynamic-experience>` and
   * does not load the UCL adapter script. Any other value uses the
   * dashboard slug as placement and shows no slow-load message.
   */
  learnersHubUclWebComponentDe:
    process.env.NEXT_PUBLIC_LEARNERS_HUB_UCL_DE === 'true',

  /**
   * Optional override for the environment passed to
   * `@highereducation/dynamic-experience`. When set to one of `local`, `dev`,
   * `int`, `beta`, or `prod` it wins over the value derived from `ENVIRONMENT`,
   * letting any stage point the DE package at a specific backend. Unset/invalid
   * values fall back to the `ENVIRONMENT`-based mapping. Must be `NEXT_PUBLIC_`
   * so it is available in the client-rendered DE adapter.
   */
  dynamicExperienceEnvironment:
    process.env.NEXT_PUBLIC_DYNAMIC_EXPERIENCE_ENV || '',

  /**
   * Business rules service origin, called only from server code. Unset (as
   * in local development without credentials), no call is made and pages
   * render their fallback slots.
   */
  businessRulesServiceUrl: process.env.BUSINESS_RULES_SERVICE_URL || '',
  /** Default deadline for one business rules call, in milliseconds. */
  businessRulesTimeoutMs: positiveInt(
    process.env.BUSINESS_RULES_TIMEOUT_MS,
    800,
  ),

  /**
   * Server-side base URL for offerings-service. Used by the
   * `/api/offerings/ranking` Route Handler to proxy sponsored card fetches
   * into the real service. Defaults to the local dev server so
   * `npm run mock:server && npm run serve` in offerings-service works
   * out of the box.
   */
  offeringsServiceUrl:
    process.env.OFFERINGS_SERVICE_URL ||
    (process.env.ENVIRONMENT === 'prd'
      ? 'https://offerings.api.highereducation.com'
      : 'https://offerings.dev.api.highereducation.com'),

  /**
   * Server-side base URL for campaign-management-service. Used by the
   * `/api/hen/v1/delivery-service/lead/conversion-init` and `.../submit` Route
   * Handlers to create/complete conversions server-side (CMS v2), keeping the
   * upstream host off the browser and letting the server set `x-forwarded-for`
   * (real client IP, used for budget/geo) and eventing headers the browser
   * cannot. `CAMPAIGN_MANAGEMENT_API_BASE_URL` overrides per environment; the
   * default is environment-aware (prod host only when `ENVIRONMENT=prd`) so a
   * non-prod stage never creates conversions against production by accident.
   */
  campaignManagementUrl:
    process.env.CAMPAIGN_MANAGEMENT_API_BASE_URL ||
    (process.env.ENVIRONMENT === 'prd'
      ? 'https://campaign-management.api.highereducation.com'
      : 'https://campaign-management.int.api.highereducation.com'),

  /**
   * Server-side base URL for the Lead Delivery System (LDS). Used by the
   * `/api/hen/v1/delivery-service/lead/submit` Route Handler to fetch the LDS v4
   * schema and submit the lead server-side. `DELIVERY_SYSTEM_API_BASE_URL`
   * overrides per environment; the default is environment-aware (prod host
   * only when `ENVIRONMENT=prd`) so production never submits leads to staging
   * by accident.
   */
  deliverySystemUrl:
    process.env.DELIVERY_SYSTEM_API_BASE_URL ||
    (process.env.ENVIRONMENT === 'prd'
      ? 'https://delivery-system.rveducation.io'
      : 'https://delivery-system.staging.rveducation.io'),

  /**
   * Server-side base URL for prospect-service. The partner pass-through
   * (`/rv-api/account/preferences/...`) sends the shopper's session token
   * here, so it must be https outside local development and tests. Unset or
   * rejected, an allowlisted pass-through call answers 503 and makes no call.
   */
  prospectServiceBaseUrl: resolveHttpsBaseUrl(
    process.env.PROSPECT_SERVICE_BASE_URL,
    process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test',
  ),

  /**
   * Learners-hub auth gate (proxy cookie check + layout validateSession).
   * Only the literal string "true" enables enforcement — unset or "false" skips SSO redirect
   * so local dev can open the hub without logging in. Use "true" in deployed environments.
   */
  eduAuthServiceEnabled: process.env.EDU_AUTH_SERVICE_ENABLED === 'true',

  /**
   * Target domain for landing-page external rewrites (e.g. Volt/Contentful proxy).
   * When empty, edge-route rules that reference this var no-op safely.
   */
  landingPageProxyDomain: process.env.LANDING_PAGE_PROXY_DOMAIN || '',

  /**
   * Server-side base URL + token for the TIP v2 geolocation API (Cohesion).
   * Used by the `/api/v1/locate` Route Handler to infer a visitor's location
   * from their IP server-side — the replacement for the deprecated Mobius
   * `/v1/locate` call. Mirrors Mobius' deployed config
   * (`API_GEOLOCATION_TIP_V2_URL` / `API_GEOLOCATION_TIP_V2_AUTH_TOKEN`), which
   * points every environment at the production Cohesion endpoint (the TIP team
   * prefers that over their staging URL). The token is server-only (never
   * `NEXT_PUBLIC_*`) so it stays off the browser; copy it from Mobius' SSM
   * parameter `/mobius/<env>/TIP_V2_AUTH_TOKEN`. When the token is absent the
   * route returns 502 and the DE client falls back to `null`.
   */
  tipApiBaseUrl:
    process.env.TIP_API_BASE_URL ||
    'https://tip.cohesionapps.com/api/v1/lookup',
  tipApiToken: process.env.TIP_API_TOKEN || '',

  /**
   * Iterable email list subscription (BC email banner BFF).
   * Server-only — never expose to the browser.
   */
  iterableApiKey: process.env.ITERABLE_API_KEY || '',
  iterableListId: process.env.ITERABLE_LIST_ID || '',
};
